import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { apiFetch } from '../api.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useFamily } from '../context/FamilyContext.jsx';
import { useEmbeddedMode, useEmbeddedNavigation } from '../hooks/useEmbeddedMode.js';
import useOnboardingStatus from '../hooks/useOnboardingStatus.js';
import useAnalytics from '../../hooks/useAnalytics.js';
import Button from '../ui/Button.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card.jsx';
import { getIconForCategory } from './QuestionnairePage.jsx';
import TaskDetailBlock from '../components/TimeBarsBlock.jsx';
import { isProfileComplete } from '../utils/profileForm.js';
import { getAppScrollContainer } from '../utils/scroll.js';
import { FaBalanceScale, FaInfoCircle, FaUser, FaUserFriends } from 'react-icons/fa';

function displayName(member) {
  return member?.name || member?.email || 'Utilisateur';
}

function formatHours(hours) {
  const h = Math.max(0, Number(hours) || 0);
  if (h >= 10) return `${Math.round(h)}h`;
  if (h >= 1) return `${h.toFixed(1).replace('.', ',')}h`;
  return `${Math.round(h * 60)} min`;
}

function formatEuro(amount) {
  const v = Number(amount) || 0;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(v);
}

function getScoreValue(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function shareScoreToSelfRatio(shareScore) {
  const value = getScoreValue(shareScore);
  if (value === null || value <= 1) return 0;
  if (value === 2) return 0.25;
  if (value === 3) return 0.5;
  if (value === 4) return 0.75;
  return 1;
}

function getMajorityOwnerFromShares(meShareScore, partnerShareScore, dominanceThreshold = 0.25) {
  const meRatio = shareScoreToSelfRatio(meShareScore);
  const partnerRatio = shareScoreToSelfRatio(partnerShareScore);
  const gap = meRatio - partnerRatio;

  if (gap >= dominanceThreshold) {
    return { owner: 'me', gap };
  }

  if (gap <= -dominanceThreshold) {
    return { owner: 'partner', gap: Math.abs(gap) };
  }

  return null;
}

function buildQuestionPath(task) {
  if (!task?.category_id || !task?.question_uuid) return null;
  return `/questionnaire/${encodeURIComponent(task.category_id)}?question=${encodeURIComponent(task.question_uuid)}`;
}

const STATS_INFO_CONTENT = {
  investment_percent: {
    title: 'Exécution',
    paragraphs: [
      "Ce chiffre représente la part de l'exécution des tâches qui repose sur vous, parmi les tâches que vous avez renseignées.",
      "Plus il est élevé, plus vous déclarez faire concrètement les tâches du quotidien.",
      "Il est calculé à partir de vos réponses sur qui fait chaque tâche, puis agrégé sur l'ensemble des tâches concernées."
    ]
  },
  mental_load_percent: {
    title: 'Charge mentale',
    paragraphs: [
      "Ce pourcentage reflète surtout qui pense aux tâches, les anticipe, les garde en tête et les organise.",
      "Il ne mesure donc pas seulement l'exécution, mais la part d'organisation mentale que vous portez.",
      "Il s'appuie sur vos réponses à la question \"Qui pense à ça ?\" sur les tâches applicables."
    ]
  },
  burden_score: {
    title: 'Pénibilité',
    paragraphs: [
      "Ce score résume à quel point les tâches que vous faites vous pèsent globalement.",
      "Il augmente surtout quand vous prenez en charge des tâches que vous jugez lourdes ou pénibles.",
      "C'est un indicateur synthétique utile pour repérer ce qui fatigue au-delà du simple temps passé."
    ]
  },
  time_week: {
    title: 'Temps',
    paragraphs: [
      "Cette estimation représente le temps domestique hebdomadaire qui vous est attribué.",
      "Elle repose sur un temps de référence par tâche, ajusté selon la part que vous dites prendre en charge et selon le contexte du foyer.",
      "C'est une estimation destinée à comparer et à visualiser les ordres de grandeur."
    ]
  },
  valuation_smic: {
    title: 'Valorisation',
    paragraphs: [
      "Cette valorisation traduit le temps domestique estimé en équivalent monétaire, ici sur la base d'un taux proche du SMIC.",
      "Elle sert surtout à rendre plus concret le volume de travail domestique invisible.",
      "Ce n'est pas un salaire réel, mais un repère pour visualiser la valeur du temps investi."
    ]
  },
  by_category_overview: {
    title: 'Aperçu par catégorie',
    paragraphs: [
      "Ce bloc compare, pour chaque grand thème du foyer, votre niveau d'investissement, votre charge mentale et la pénibilité ressentie.",
      "Il permet de repérer rapidement les catégories où vous faites beaucoup, pensez beaucoup ou subissez plus de charge.",
      "Les scores sont regroupés par catégorie à partir des réponses données sur les tâches qui la composent."
    ]
  },
  time_distribution: {
    title: 'Répartition de mon temps',
    paragraphs: [
      "Cette vue montre comment votre semaine théorique se répartit entre sommeil, travail, transports, tâches domestiques et temps libre.",
      "Elle combine les informations de votre profil avec le temps domestique estimé dans le questionnaire.",
      "Le but est de replacer la charge domestique dans l'équilibre global de votre semaine."
    ]
  },
  time_by_category: {
    title: 'Temps par catégorie',
    paragraphs: [
      "Ce graphique répartit votre temps domestique estimé entre les grands thèmes du foyer.",
      "Il aide à voir quels domaines prennent le plus de place dans votre charge réelle.",
      "Il est construit à partir des estimations de temps par tâche, regroupées par catégorie."
    ]
  },
  burden_distribution: {
    title: 'Répartition de la pénibilité',
    paragraphs: [
      "Cette vue met en avant les catégories qui concentrent le plus de pénibilité ressentie.",
      "Elle ne parle pas seulement du temps passé, mais aussi du poids subjectif des tâches.",
      "Une catégorie peut donc ressortir même si elle n'est pas la plus longue, si elle est particulièrement pesante."
    ]
  },
  task_details: {
    title: 'Détail par tâche',
    paragraphs: [
      "Cette liste montre les tâches une par une, avec une estimation du temps associé et leur niveau de pénibilité.",
      "Elle permet d'identifier concrètement ce qui prend du temps ou ce qui pèse le plus.",
      "C'est la vue la plus opérationnelle pour comprendre où agir en priorité."
    ]
  },
  comparison_category_imbalances: {
    title: 'Déséquilibres par catégorie',
    paragraphs: [
      "Ce bloc compare, catégorie par catégorie, la part de temps estimée prise en charge par chacun.",
      "Il fait ressortir les thèmes où l'écart entre vous deux est le plus marqué.",
      "Il sert surtout à repérer les grands déséquilibres plutôt qu'à lire chaque minute au détail près."
    ]
  },
  comparison_swaps: {
    title: "Opportunités d'échange",
    paragraphs: [
      "Ce bloc suggère des tâches qui pourraient être redistribuées.",
      "Il repère surtout les cas où une tâche pèse davantage à l'un qu'à l'autre, ce qui peut ouvrir une discussion utile.",
      "L'idée n'est pas d'imposer une règle, mais de pointer des pistes concrètes de rééquilibrage."
    ]
  },
  comparison_curiosities: {
    title: 'Curiosités',
    paragraphs: [
      "Cette section met en avant des décalages intéressants entre la perception d'une tâche et sa réalisation concrète.",
      "Par exemple, quelqu'un peut aimer une tâche sans pourtant être la personne qui la fait le plus.",
      "Ce sont des signaux de discussion, pas des anomalies au sens strict."
    ]
  },
  comparison_anomalies: {
    title: 'Anomalies de perception',
    paragraphs: [
      "Ce bloc repère des réponses très contradictoires ou des écarts de ressenti marqués entre vous deux.",
      "Quand chacun pense faire plus, ou que personne ne se sent responsable, cela peut révéler un vrai sujet de coordination.",
      "L'objectif est d'aider à repérer des malentendus ou des zones floues dans l'organisation."
    ]
  },
  comparison_mental_rebalance: {
    title: 'Rééquilibrage de charge mentale',
    paragraphs: [
      "Ce bloc cherche les cas où l'anticipation d'une tâche liée semble portée par une personne, tandis que l'exécution est surtout assurée par l'autre.",
      "Il s'appuie sur les paires de tâches liées que nous avons définies, par exemple planifier les repas puis préparer les repas.",
      "Il sert à rendre visible le partage entre charge d'organisation et charge d'exécution."
    ]
  }
};

function StatsInfoButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="shrink-0 rounded-full p-1 text-ptcm-muted transition-colors hover:text-ptcm-primary dark:text-gray-400 dark:hover:text-blue-400"
    >
      <FaInfoCircle className="h-4 w-4" />
    </button>
  );
}

function SectionTitleWithInfo({ children, infoKey, onOpenInfo, className = '' }) {
  return (
    <CardTitle className={`flex items-center justify-between gap-3 ${className}`}>
      <span className="flex min-w-0 items-center gap-2">{children}</span>
      <StatsInfoButton
        label={`Explication : ${STATS_INFO_CONTENT[infoKey]?.title || 'statistique'}`}
        onClick={() => onOpenInfo(infoKey)}
      />
    </CardTitle>
  );
}

function StatsInfoModal({ infoKey, onClose }) {
  const embedded = useEmbeddedMode();
  const info = infoKey ? STATS_INFO_CONTENT[infoKey] : null;
  const [render, setRender] = useState(Boolean(info));
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (info) {
      setRender(true);
      requestAnimationFrame(() => setVisible(true));
      document.body.style.overflow = 'hidden';
      return undefined;
    }

    setVisible(false);
    const timer = setTimeout(() => {
      setRender(false);
      document.body.style.overflow = '';
    }, 200);
    return () => clearTimeout(timer);
  }, [info]);

  useEffect(() => {
    if (!info) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [info, onClose]);

  if (!render || !info) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[140] flex items-center justify-center px-3 sm:px-4">
      <button
        type="button"
        aria-label="Fermer l'aide"
        onClick={onClose}
        className={`absolute inset-0 transition-opacity duration-200 ${embedded ? 'bg-transparent' : 'bg-black/20 backdrop-blur-sm'} ${visible ? 'opacity-100' : 'opacity-0'}`}
      />
      <div
        className={`relative w-full ${embedded ? 'max-w-sm rounded-[24px] p-4' : 'max-w-md rounded-[28px] p-5'} border border-ptcm-border/80 bg-white text-left shadow-ptcm-soft transition-all duration-200 dark:border-gray-800 dark:bg-gray-900 ${visible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-2 scale-95 opacity-0'}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className={`${embedded ? 'text-[15px]' : 'text-base'} font-semibold text-ptcm-ink dark:text-gray-100`}>
            {info.title}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-ptcm-muted transition-colors hover:text-ptcm-ink dark:text-gray-400 dark:hover:text-gray-200"
            aria-label="Fermer"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={`mt-3 ${embedded ? 'space-y-2 max-h-[44svh]' : 'space-y-3 max-h-[60vh]'} overflow-y-auto pr-1 custom-scrollbar`}>
          {info.paragraphs.map((paragraph, index) => (
            <p key={`${infoKey}-${index}`} className={`${embedded ? 'text-[13px] leading-5' : 'text-sm leading-6'} text-ptcm-ink dark:text-gray-200`}>
              {paragraph}
            </p>
          ))}
        </div>
        {!embedded ? (
          <div className="mt-5 flex justify-end">
            <Button size="sm" onClick={onClose}>
              Compris
            </Button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

function KpiCard({ title, value, subtitle, color = "bg-white dark:bg-gray-900", infoKey, onOpenInfo }) {
  return (
    <div
      className={`shrink-0 w-32 snap-center rounded-[24px] border border-ptcm-border dark:border-gray-800 ${color} p-4 flex flex-col justify-between shadow-ptcm-card transition-shadow`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-medium text-ptcm-muted dark:text-gray-400 uppercase tracking-wide leading-tight line-clamp-2 min-h-[1.9em]">
          {title}
        </div>
        {infoKey && onOpenInfo ? (
          <StatsInfoButton
            label={`Explication : ${STATS_INFO_CONTENT[infoKey]?.title || title}`}
            onClick={() => onOpenInfo(infoKey)}
          />
        ) : null}
      </div>
      <div className="mt-2 text-2xl font-bold tracking-tight text-ptcm-ink dark:text-gray-100">
        {value}
      </div>
      {subtitle ? <div className="mt-1 text-[10px] text-ptcm-muted/80 dark:text-gray-500">{subtitle}</div> : null}
    </div>
  );
}
function ViewSelector({ view, onChange, partnerDisabled, partnerName, condensed = false }) {
  const options = [
    {
      id: 'me',
      label: 'Moi',
      shortDescription: 'Mes stats',
      icon: FaUser
    },
    {
      id: 'partner',
      label: 'Conjoint·e',
      shortDescription: partnerName ? `Voir ${partnerName}` : 'Ses stats',
      icon: FaUserFriends
    },
    {
      id: 'compare',
      label: 'Comparaison',
      shortDescription: 'Voir les écarts',
      icon: FaBalanceScale
    },
  ];

  return (
    <div className={`w-full rounded-[28px] border border-ptcm-border dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 shadow-ptcm-card transition-all ${condensed ? 'px-2 py-1.5' : 'p-3'}`}>
      {!condensed ? (
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-ptcm-muted dark:text-gray-400">
              Affichage
            </div>
          </div>
        </div>
      ) : null}

      <div className={`grid grid-cols-3 ${condensed ? 'gap-1.5' : 'gap-2'}`}>
        {options.map((opt) => {
          const isActive = view === opt.id;
          const isDisabled = (opt.id === 'partner' || opt.id === 'compare') && partnerDisabled;
          const Icon = opt.icon;

          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => !isDisabled && onChange(opt.id)}
              disabled={isDisabled}
              aria-pressed={isActive}
              className={`
                rounded-xl border transition-all
                ${condensed ? 'min-h-[44px] px-2 py-2 text-center' : 'min-h-[72px] px-3 py-3 text-left'}
                ${condensed ? 'flex items-center justify-center gap-2' : 'flex flex-col justify-between gap-2'}
                ${isDisabled ? 'cursor-not-allowed border-ptcm-border dark:border-gray-800 bg-ptcm-panel dark:bg-gray-900/70 text-ptcm-muted dark:text-gray-500 opacity-75' : ''}
                ${
                  isActive
                    ? 'border-ptcm-primary bg-ptcm-primary text-white shadow-ptcm-float'
                    : 'border-ptcm-border dark:border-gray-700 bg-white dark:bg-gray-950 text-ptcm-ink dark:text-gray-200 hover:border-ptcm-primary/30 hover:bg-ptcm-primary-soft/35 dark:hover:bg-gray-800'
                }
              `}
            >
              {condensed ? (
                <>
                  <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-white' : 'text-ptcm-primary dark:text-blue-400'}`} />
                  <span className={`text-xs font-semibold leading-none ${isActive ? 'text-white' : 'text-ptcm-ink dark:text-gray-100'}`}>
                    {opt.label}
                  </span>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-ptcm-primary dark:text-blue-400'}`} />
                    {isDisabled ? (
                      <span className={`text-[10px] font-semibold uppercase tracking-wide ${isActive ? 'text-white/80' : 'text-ptcm-muted dark:text-gray-500'}`}>
                        Bientôt
                      </span>
                    ) : null}
                  </div>
                  <div>
                    <div className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-ptcm-ink dark:text-gray-100'}`}>
                      {opt.label}
                    </div>
                    <div className={`mt-0.5 text-[11px] leading-tight ${isActive ? 'text-white/85' : 'text-ptcm-muted dark:text-gray-400'}`}>
                      {opt.shortDescription}
                    </div>
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>

      {partnerDisabled && !condensed ? (
        <div className="mt-3 rounded-[22px] border border-amber-200/70 dark:border-amber-900/30 bg-amber-50/90 dark:bg-amber-900/10 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-200">
          Les vues Conjoint·e et Comparaison seront disponibles dès que votre partenaire aura rejoint le foyer.
        </div>
      ) : null}
    </div>
  );
}

function ComparisonInsightCard({ item, tone = 'neutral', onOpen }) {
  const toneClasses = {
    success: 'border border-ptcm-border bg-ptcm-mint/55 dark:bg-emerald-900/10 dark:border-emerald-900/30',
    warning: 'border border-amber-200/70 bg-amber-50/90 dark:bg-amber-900/10 dark:border-amber-900/30',
    danger: 'border border-red-200/70 bg-red-50/90 dark:bg-red-900/10 dark:border-red-900/30',
    accent: 'border border-violet-200/70 bg-violet-50/90 dark:bg-violet-900/10 dark:border-violet-900/30',
    neutral: 'border border-ptcm-border bg-white dark:bg-gray-900 dark:border-gray-800'
  };
  const detailClasses = {
    success: 'text-xs font-medium text-emerald-700 dark:text-emerald-400',
    warning: 'text-xs text-amber-700 dark:text-amber-400',
    danger: 'text-xs text-red-700 dark:text-red-300',
    accent: 'text-xs text-violet-700 dark:text-violet-300',
    neutral: 'text-xs text-ptcm-ink/80 dark:text-gray-300'
  };
  const nextPath = buildQuestionPath(item);
  const isClickable = Boolean(nextPath && onOpen);
  const content = (
    <>
      <div className="text-sm font-bold text-ptcm-ink dark:text-gray-100">{item.task}</div>
      <div className="mb-1 text-xs text-ptcm-muted">{item.cat}</div>
      <div className={detailClasses[tone] || detailClasses.neutral}>
        {item.desc || item.reason}
      </div>
    </>
  );

  if (!isClickable) {
    return (
      <div className={`rounded-[22px] p-3 ${toneClasses[tone] || toneClasses.neutral}`}>
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={`w-full rounded-[22px] p-3 text-left transition-transform hover:-translate-y-0.5 ${toneClasses[tone] || toneClasses.neutral}`}
    >
      {content}
    </button>
  );
}

function StatsLoadingState({ partnerDisabled, partnerName, viewMode, onViewModeChange }) {
  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-4 pb-20 space-y-6">
      <div
        className="sticky z-20 -mx-3 sm:-mx-4 px-3 sm:px-4 py-2 border-b border-ptcm-border/70 dark:border-gray-800 bg-ptcm-bg/80 dark:bg-gray-950/80 backdrop-blur-xl"
        style={{ top: 'var(--ptcm-sticky-top-offset)' }}
      >
        <div className="max-w-5xl mx-auto">
          <ViewSelector
            view={viewMode}
            onChange={onViewModeChange}
            partnerDisabled={partnerDisabled}
            partnerName={partnerName}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-pulse">
        <Card>
          <CardHeader>
            <div className="h-6 w-40 rounded-full bg-ptcm-panel dark:bg-gray-800" />
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="h-24 rounded-[24px] bg-ptcm-panel dark:bg-gray-800" />
            <div className="h-24 rounded-[24px] bg-ptcm-panel dark:bg-gray-800" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="h-6 w-48 rounded-full bg-ptcm-panel dark:bg-gray-800" />
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="h-10 rounded-full bg-ptcm-panel dark:bg-gray-800" />
            <div className="h-10 rounded-full bg-ptcm-panel dark:bg-gray-800" />
            <div className="h-10 rounded-full bg-ptcm-panel dark:bg-gray-800" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ComparisonBlock({ meData, partnerData, onOpenInfo }) {
  const { navigateEmbedded } = useEmbeddedNavigation();

  const openTask = (task) => {
    const nextPath = buildQuestionPath(task);
    if (nextPath) {
      navigateEmbedded(nextPath);
    }
  };

  // 1. Categories Imbalance (Delta in hours)
  const categoryDeltas = useMemo(() => {
    if (!meData || !partnerData) return [];

    const meCats = meData.by_category || [];
    const partnerCats = partnerData.by_category || [];

    // Create a map of category_id -> { label, meMinutes, partnerMinutes }
    const map = new Map();

    meCats.forEach(c => {
       map.set(c.category_id, {
           id: c.category_id,
           label: c.category_label,
           me: c.minutes_per_week || 0,
           partner: 0
       });
    });

    partnerCats.forEach(c => {
       if (!map.has(c.category_id)) {
           map.set(c.category_id, {
               id: c.category_id,
               label: c.category_label,
               me: 0,
               partner: 0
           });
       }
       const entry = map.get(c.category_id);
       entry.partner = c.minutes_per_week || 0;
    });

    return Array.from(map.values())
        .map(c => ({ ...c, delta: c.me - c.partner, absDelta: Math.abs(c.me - c.partner) }))
        .sort((a, b) => b.absDelta - a.absDelta)
        .slice(0, 5); // Top 5 divergences
  }, [meData, partnerData]);

  // 2. Task Analysis
  const analysis = useMemo(() => {
    if (!meData || !partnerData) {
      return {
        swaps: [],
        mismatches: [],
        anomalies: {
          bothDoMore: [],
          bothThinkOtherDoesMore: [],
          bothDoNotHandle: []
        },
        mentalLoadRebalances: []
      };
    }

    const meTasks = meData.by_task || [];
    const partnerTasks = partnerData.by_task || [];

    const partnerTaskMap = new Map(partnerTasks.map(t => [t.question_uuid, t]));

    const swaps = []; // Complementary
    const mismatches = []; // Weird distribution
    const anomalies = {
      bothDoMore: [],
      bothThinkOtherDoesMore: [],
      bothDoNotHandle: []
    };
    const mentalLoadRebalances = [];
    const linkedTaskGroups = new Map();

    meTasks.forEach(meT => {
        const partnerT = partnerTaskMap.get(meT.question_uuid);
        if (!partnerT) return;

        // Scores
        // Burden: 1 (J'aime), 2 (Ne pèse pas), 3 (Un peu), 4 (Beaucoup)
        // Minutes: meT.minutes_per_week

        const meB = meT.burden_score;
        const patB = partnerT.burden_score;
        const meTime = Number(meT.minutes_per_week) || 0;
        const patTime = Number(partnerT.minutes_per_week) || 0;
        const meShare = getScoreValue(meT.share_score);
        const partnerShare = getScoreValue(partnerT.share_score);
        const baseTask = {
          question_uuid: meT.question_uuid,
          category_id: meT.category_id,
          is_personal: meT.is_personal === true,
          task: meT.question_label,
          cat: meT.category_label
        };

        // A. Complementarity (Swaps)
        // Case 1: Weights on Me (>=3), Light on Partner (<=2) -> Delegate to Partner
        if (meB >= 3 && patB <= 2 && patB > 0) {
            swaps.push({
                ...baseTask,
                type: 'to_partner',
                reason: "Vous pèse mais pas à votre conjoint·e"
            });
        }
        // Case 2: Weights on Partner (>=3), Light on Me (<=2) -> Delegate to Me
        if (patB >= 3 && meB <= 2 && meB > 0) {
             swaps.push({
                ...baseTask,
                type: 'to_me',
                reason: "Pèse à votre conjoint·e mais pas à vous"
            });
        }

        // B. Mismatches (Paradoxes)
        // Case 1: I like it (1), but Partner does it more
        // (Assuming "Doing it more" means significant time difference)
        if (meB === 1 && patTime > meTime + 30) { // +30min diff threshold
            mismatches.push({
                ...baseTask,
                desc: "Vous aimez cette tâche, pourtant c'est votre conjoint·e qui la fait majoritairement."
            });
        }
        // Case 2: Partner likes it (1), but I do it more
        if (patB === 1 && meTime > patTime + 30) {
            mismatches.push({
                ...baseTask,
                desc: "Votre conjoint·e aime cette tâche, pourtant c'est vous qui la faites majoritairement."
            });
        }

        if (meT.is_personal !== true && meShare !== null && partnerShare !== null) {
          if (meShare >= 4 && partnerShare >= 4) {
            anomalies.bothDoMore.push({
              ...baseTask,
              desc: "Vous avez tous les deux le sentiment d'en faire plus que l'autre."
            });
          }

          if (meShare === 2 && partnerShare === 2) {
            anomalies.bothThinkOtherDoesMore.push({
              ...baseTask,
              desc: "Vous avez tous les deux le sentiment que c'est l'autre qui en fait plus."
            });
          }

          if (meShare === 1 && partnerShare === 1) {
            anomalies.bothDoNotHandle.push({
              ...baseTask,
              desc: "Vous dites tous les deux ne pas vous en occuper."
            });
          }
        }

        if (meT.linked_group_id && (meT.linked_order === 'precedes' || meT.linked_order === 'follows')) {
          const existingGroup = linkedTaskGroups.get(meT.linked_group_id) || {
            precedes: null,
            follows: null
          };

          existingGroup[meT.linked_order] = {
            meTask: meT,
            partnerTask: partnerT
          };
          linkedTaskGroups.set(meT.linked_group_id, existingGroup);
        }
    });

    linkedTaskGroups.forEach(({ precedes, follows }) => {
      if (!precedes || !follows) return;

      const planningOwner = getMajorityOwnerFromShares(
        precedes.meTask.share_score,
        precedes.partnerTask.share_score
      );
      const executionOwner = getMajorityOwnerFromShares(
        follows.meTask.share_score,
        follows.partnerTask.share_score
      );

      if (!planningOwner || !executionOwner || planningOwner.owner === executionOwner.owner) {
        return;
      }

      const planningTaskLabel = precedes.meTask.question_label;
      const executionTaskLabel = follows.meTask.question_label;
      const openedTask = planningOwner.owner === 'me' ? precedes.meTask : follows.meTask;
      const severity = planningOwner.gap + executionOwner.gap;

      if (planningOwner.owner === 'me' && executionOwner.owner === 'partner') {
        mentalLoadRebalances.push({
          question_uuid: openedTask.question_uuid,
          category_id: openedTask.category_id,
          task: executionTaskLabel,
          cat: follows.meTask.category_label,
          severity,
          desc: `Vous semblez porter majoritairement l'anticipation via "${planningTaskLabel}", tandis que votre conjoint·e assure surtout l'exécution via "${executionTaskLabel}".`
        });
      }

      if (planningOwner.owner === 'partner' && executionOwner.owner === 'me') {
        mentalLoadRebalances.push({
          question_uuid: openedTask.question_uuid,
          category_id: openedTask.category_id,
          task: executionTaskLabel,
          cat: follows.meTask.category_label,
          severity,
          desc: `Votre conjoint·e semble porter majoritairement l'anticipation via "${planningTaskLabel}", tandis que vous assurez surtout l'exécution via "${executionTaskLabel}".`
        });
      }
    });

    mentalLoadRebalances.sort((a, b) => (b.severity || 0) - (a.severity || 0));

    return { swaps, mismatches, anomalies, mentalLoadRebalances };
  }, [meData, partnerData]);

  if (!meData || !partnerData) return null;

  return (
    <div className="space-y-8">
        {/* Info Bubble / Explanation */}
        <div className="rounded-[24px] border border-ptcm-border bg-ptcm-primary-soft/70 p-4 dark:bg-blue-900/20 dark:border-blue-800">
            <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-ptcm-ink dark:text-blue-100">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                Comprendre cette vue
            </h3>
            <p className="text-sm text-ptcm-muted dark:text-blue-200">
                Cette page met en lumière les déséquilibres et les opportunités. Découvrez les catégories où la répartition est inégale, et les tâches que vous pourriez échanger pour alléger la charge mentale globale du foyer. Touchez une carte pour rouvrir directement la question associée.
            </p>
        </div>

        {/* 1. Category Imbalances */}
        <Card>
            <CardHeader>
              <SectionTitleWithInfo infoKey="comparison_category_imbalances" onOpenInfo={onOpenInfo}>
                Déséquilibres par catégorie (Top 5)
              </SectionTitleWithInfo>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {categoryDeltas.map(c => {
                        const total = c.me + c.partner;
                        const mePct = total > 0 ? (c.me / total) * 100 : 0;
                        const patPct = total > 0 ? (c.partner / total) * 100 : 0;

                        return (
                            <div key={c.id} className="space-y-1">
                                <div className="flex justify-between text-sm">
                                    <span className="font-medium text-ptcm-ink dark:text-gray-300">{c.label}</span>
                                    <span className="text-ptcm-muted text-xs">{formatHours(Math.abs(c.delta)/60)} d'écart</span>
                                </div>
                                <div className="h-6 w-full bg-ptcm-panel dark:bg-gray-800 rounded-full overflow-hidden flex relative">
                                    {/* Me Bar */}
                                    <div
                                        style={{ width: `${mePct}%` }}
                                        className="h-full bg-ptcm-primary flex items-center justify-center transition-all"
                                    >
                                        {mePct >= 15 && <span className="text-[10px] sm:text-xs font-bold text-white truncate px-1">{Math.round(mePct)}% Moi</span>}
                                    </div>

                                    {/* Partner Bar */}
                                    <div
                                        style={{ width: `${patPct}%` }}
                                        className="h-full bg-ptcm-sand flex items-center justify-center transition-all"
                                    >
                                        {patPct >= 15 && <span className="text-[10px] sm:text-xs font-bold text-white truncate px-1">{Math.round(patPct)}% Conj.</span>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 2. Swaps (Complementarity) */}
            <Card className="h-full">
                <CardHeader>
                    <SectionTitleWithInfo infoKey="comparison_swaps" onOpenInfo={onOpenInfo} className="flex items-center gap-2">
                        <span className="text-emerald-600">🤝</span>
                        Opportunités d'échange
                    </SectionTitleWithInfo>
                </CardHeader>
                <CardContent>
                    {analysis.swaps.length === 0 ? (
                        <p className="text-sm text-ptcm-muted">Aucune opportunité évidente détectée pour le moment.</p>
                    ) : (
                        <div className="space-y-3">
                            {analysis.swaps.map((s, i) => (
                                <ComparisonInsightCard
                                  key={`${s.question_uuid}-${s.type}-${i}`}
                                  item={{
                                    ...s,
                                    reason: `${s.type === 'to_partner' ? 'À déléguer au conjoint' : 'À récupérer'} : ${s.reason}`
                                  }}
                                  tone="success"
                                  onOpen={openTask}
                                />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* 3. Mismatches */}
            <Card className="h-full">
                <CardHeader>
                    <SectionTitleWithInfo infoKey="comparison_curiosities" onOpenInfo={onOpenInfo} className="flex items-center gap-2">
                        <span className="text-amber-500">🤔</span>
                        Curiosités
                    </SectionTitleWithInfo>
                </CardHeader>
                <CardContent>
                    {analysis.mismatches.length === 0 ? (
                         <p className="text-sm text-ptcm-muted">Aucune incohérence majeure détectée.</p>
                    ) : (
                        <div className="space-y-3">
                            {analysis.mismatches.map((m, i) => (
                                <ComparisonInsightCard
                                  key={`${m.question_uuid}-${i}`}
                                  item={m}
                                  tone="warning"
                                  onOpen={openTask}
                                />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="h-full">
                <CardHeader>
                    <SectionTitleWithInfo infoKey="comparison_anomalies" onOpenInfo={onOpenInfo} className="flex items-center gap-2">
                        <span className="text-red-500">⚠️</span>
                        Anomalies de perception
                    </SectionTitleWithInfo>
                </CardHeader>
                <CardContent className="space-y-5">
                    {analysis.anomalies.bothDoMore.length === 0
                      && analysis.anomalies.bothThinkOtherDoesMore.length === 0
                      && analysis.anomalies.bothDoNotHandle.length === 0 ? (
                        <p className="text-sm text-ptcm-muted">Aucune anomalie flagrante détectée pour le moment.</p>
                      ) : (
                        <>
                          <div className="space-y-3">
                            <div className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
                              Chacun dit en faire plus
                            </div>
                            {analysis.anomalies.bothDoMore.length === 0 ? (
                              <p className="text-sm text-ptcm-muted">Aucune tâche dans ce cas.</p>
                            ) : (
                              analysis.anomalies.bothDoMore.map((item, index) => (
                                <ComparisonInsightCard
                                  key={`anomaly-more-${item.question_uuid}-${index}`}
                                  item={item}
                                  tone="danger"
                                  onOpen={openTask}
                                />
                              ))
                            )}
                          </div>

                          <div className="space-y-3">
                            <div className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
                              Chacun dit que c&apos;est l&apos;autre qui en fait plus
                            </div>
                            {analysis.anomalies.bothThinkOtherDoesMore.length === 0 ? (
                              <p className="text-sm text-ptcm-muted">Aucune tâche dans ce cas.</p>
                            ) : (
                              analysis.anomalies.bothThinkOtherDoesMore.map((item, index) => (
                                <ComparisonInsightCard
                                  key={`anomaly-other-${item.question_uuid}-${index}`}
                                  item={item}
                                  tone="danger"
                                  onOpen={openTask}
                                />
                              ))
                            )}
                          </div>

                          <div className="space-y-3">
                            <div className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
                              Chacun dit ne pas s&apos;en occuper
                            </div>
                            {analysis.anomalies.bothDoNotHandle.length === 0 ? (
                              <p className="text-sm text-ptcm-muted">Aucune tâche dans ce cas.</p>
                            ) : (
                              analysis.anomalies.bothDoNotHandle.map((item, index) => (
                                <ComparisonInsightCard
                                  key={`anomaly-none-${item.question_uuid}-${index}`}
                                  item={item}
                                  tone="danger"
                                  onOpen={openTask}
                                />
                              ))
                            )}
                          </div>
                        </>
                      )}
                </CardContent>
            </Card>

            <Card className="h-full">
                <CardHeader>
                    <SectionTitleWithInfo infoKey="comparison_mental_rebalance" onOpenInfo={onOpenInfo} className="flex items-center gap-2">
                        <span className="text-violet-500">🧠</span>
                        Rééquilibrage de charge mentale
                    </SectionTitleWithInfo>
                </CardHeader>
                <CardContent>
                    {analysis.mentalLoadRebalances.length === 0 ? (
                        <p className="text-sm text-ptcm-muted">Aucun décalage net entre exécution et charge mentale.</p>
                    ) : (
                        <div className="space-y-3">
                            {analysis.mentalLoadRebalances.map((item, index) => (
                                <ComparisonInsightCard
                                  key={`mental-rebalance-${item.question_uuid}-${index}`}
                                  item={item}
                                  tone="accent"
                                  onOpen={openTask}
                                />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    </div>
  );
}

function DualBarRow({ label, investmentValue, mentalValue, burdenValue }) {
  const pInvestment = Math.max(0, Math.min(100, Number(investmentValue) || 0));
  const pMental = Math.max(0, Math.min(100, Number(mentalValue) || 0));
  const pBurden = Math.max(0, Math.min(100, Number(burdenValue) || 0));

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate pr-2">{label}</div>
      </div>
      <div className="space-y-1">
        <div className="flex items-center gap-3">
        <div className="w-full h-2 bg-ptcm-panel dark:bg-gray-800 rounded-full overflow-hidden flex-1">
                <div className="h-2 rounded-full bg-ptcm-primary" style={{ width: `${pInvestment}%` }} />
            </div>
            <div className="w-8 text-right text-xs font-medium text-ptcm-primary dark:text-blue-400">{pInvestment}%</div>
        </div>
        <div className="flex items-center gap-3">
            <div className="w-full h-2 bg-ptcm-panel dark:bg-gray-800 rounded-full overflow-hidden flex-1">
                <div className="h-2 rounded-full bg-ptcm-sand" style={{ width: `${pMental}%` }} />
            </div>
            <div className="w-8 text-right text-xs font-medium text-ptcm-sand dark:text-violet-400">{pMental}%</div>
        </div>
        <div className="flex items-center gap-3">
            <div className="w-full h-2 bg-ptcm-panel dark:bg-gray-800 rounded-full overflow-hidden flex-1">
                <div className="h-2 rounded-full bg-amber-500" style={{ width: `${pBurden}%` }} />
            </div>
            <div className="w-8 text-right text-xs font-medium text-amber-600 dark:text-amber-400">{Math.round(pBurden)}</div>
        </div>
      </div>
    </div>
  );
}

function DualBarsBlock({ items }) {
  if (!items || items.length === 0) {
    return (
      <div className="rounded-[24px] border border-ptcm-border dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="mt-2 text-sm text-ptcm-muted dark:text-gray-400">
          Aucune catégorie applicable entièrement complétée pour le moment.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900/50 p-4 space-y-4">
      <div className="flex items-center gap-4 text-xs font-medium mb-2 flex-wrap">
        <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-ptcm-primary"></div>
            <span className="text-ptcm-muted dark:text-gray-400">Investissement</span>
        </div>
        <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-ptcm-sand"></div>
            <span className="text-ptcm-muted dark:text-gray-400">Charge mentale</span>
        </div>
        <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-amber-500"></div>
            <span className="text-ptcm-muted dark:text-gray-400">Pénibilité</span>
        </div>
      </div>

      <div className="space-y-5">
        {items.map((c) => (
          <DualBarRow
            key={c.category_id}
            label={c.category_label}
            investmentValue={c.investment_percent}
            mentalValue={c.mental_load_percent}
            burdenValue={c.burden_percent}
          />
        ))}
      </div>
    </div>
  );
}

function TimeDistributionBlock({ profile, householdTasksHours }) {
  if (!profile) return null;

  const totalWeek = 168;
  const sleep = (profile.sleep_hours_per_night || 0) * 7;
  const work = profile.work_hours_per_week || 0;
  const commute = profile.commute_hours_per_week || 0;
  const household = householdTasksHours || 0;

  let free = totalWeek - sleep - work - commute - household;
  if (free < 0) free = 0;

  const data = [
    { name: 'Sommeil', value: sleep, color: '#8b5cf6' }, // Violet 500
    { name: 'Travail', value: work, color: '#3b82f6' }, // Blue 500
    { name: 'Transports', value: commute, color: '#64748b' }, // Slate 500
    { name: 'Tâches domestiques', value: household, color: '#f59e0b' }, // Amber 500
    { name: 'Temps libre', value: free, color: '#10b981' }, // Emerald 500
  ].filter(d => d.value > 0);

  return (
    <div className="flex flex-col sm:flex-row items-center gap-6">
      <div className="w-full sm:w-1/2 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={80}
              paddingAngle={5}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={0} />
              ))}
            </Pie>
            <Tooltip
                formatter={(value) => [`${formatHours(value)}`, 'Durée']}
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="w-full sm:w-1/2 space-y-3">
        {data.map((item) => (
          <div key={item.name} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
              <span className="text-ptcm-ink dark:text-gray-300">{item.name}</span>
            </div>
            <div className="font-medium text-ptcm-ink dark:text-gray-100">
              {formatHours(item.value)} <span className="text-xs font-normal text-ptcm-muted">({Math.round(item.value / totalWeek * 100)}%)</span>
            </div>
          </div>
        ))}
        <div className="pt-2 mt-2 border-t border-ptcm-border dark:border-gray-800 flex justify-between text-sm font-medium">
            <span className="text-ptcm-muted">Total semaine</span>
            <span className="text-ptcm-ink dark:text-gray-100">168h</span>
        </div>
      </div>
    </div>
  );
}

function CategoryDistributionBlock({ categories }) {
  if (!categories || categories.length === 0) return null;

  // 1. Time distribution by category
  const timeData = categories
    .map(c => ({
      name: c.category_label,
      value: c.minutes_per_week || 0,
      color: '#' + Math.floor(Math.random()*16777215).toString(16) // Random color for now, could be better
    }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);

  // Better colors for categories
  const COLORS = [
    '#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316',
    '#eab308', '#84cc16', '#22c55e', '#10b981', '#06b6d4',
    '#0ea5e9', '#6366f1'
  ];

  timeData.forEach((d, i) => d.color = COLORS[i % COLORS.length]);

  // Sort data descending by value
  timeData.sort((a, b) => b.value - a.value);

  // 2. Burden distribution (average burden score per category)
  // burden_score_sum / applicable_tasks_count ? No we don't have sum directly in frontend props
  // But we have `burden_percent` in the `items` passed to DualBarsBlock (which is `selectedByCategory` from backend)
  // Let's use `selectedByCategory` which contains `burden_percent`
  // Wait, the input `categories` here is `timeCategories` which comes from `time.by_category`.
  // `time.by_category` has `minutes_per_week`.
  // We need `selectedByCategory` (from kpis) for burden.

  return (
    <div className="flex flex-col gap-6">
      {/* Time by Category */}
      <div className="flex-1 space-y-4">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={timeData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {timeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={0} />
                ))}
              </Pie>
              <Tooltip
                  formatter={(value) => [`${formatHours(value / 60)}`, 'Durée']}
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
            {timeData.map((item) => (
            <div key={item.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                <span className="text-ptcm-ink dark:text-gray-300 truncate">{item.name}</span>
                </div>
                <div className="font-medium text-ptcm-ink dark:text-gray-100 whitespace-nowrap">
                {formatHours(item.value / 60)}
                </div>
            </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function BurdenDistributionBlock({ kpiCategories }) {
    if (!kpiCategories || kpiCategories.length === 0) return null;

    // We want "Répartition de la pénibilité de chaque catégorie"
    // "sommant la pénibilité de chaque tâche et diviser par le nombre de tâches concernées"
    // Backend `burden_percent` is already: (sum(burden_weighted) / max_possible_burden) * 100
    // This represents the "intensity" of burden for that category.
    // If we want a pie chart, we probably want to compare the *relative* burden of each category vs total burden.
    // Or maybe just show the intensity? A pie chart shows parts of a whole.
    // "Répartition de la pénibilité" suggests parts of a whole (e.g. "50% of my total suffering comes from Cleaning").
    // To do that, we need the raw burden score sum per category, not the percent.
    // The backend `computeCategoryKpisForUser` returns `burden_percent`.
    // It does NOT return the raw sum.
    // However, `investment_percent` is roughly proportional to sum if max is constant, but max depends on task count.
    // `burden_percent` = (sum / (n*8)) * 100. So `sum` ~= `burden_percent` * n * 0.08.
    // Let's approximate the "Total Burden Volume" as `burden_percent * applicable_tasks_count`.

    const data = kpiCategories
        .map(c => ({
            name: c.category_label,
            value: (c.burden_percent || 0) * (c.applicable_tasks_count || 0),
            color: '#f59e0b'
        }))
        .filter(d => d.value > 0)
        .sort((a, b) => b.value - a.value);

    // Use same colors as time chart if possible, but we don't have the same order/items potentially.
    // Let's generate colors.
    const COLORS = [
        '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
        '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
        '#8b5cf6', '#d946ef'
    ];
    data.forEach((d, i) => d.color = COLORS[i % COLORS.length]);

    // Sort descending
    data.sort((a, b) => b.value - a.value);

    if (data.length === 0) return (
        <div className="flex items-center justify-center h-full text-sm text-gray-500">
            Aucune pénibilité significative détectée.
        </div>
    );

    return (
      <div className="flex-1 space-y-4">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={0} />
                ))}
              </Pie>
              <Tooltip
                  formatter={(value, name, props) => {
                      const total = data.reduce((a, b) => a + b.value, 0);
                      return [`${Math.round(value / total * 100)}%`, 'Part de la pénibilité'];
                  }}
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
            {data.map((item) => {
                const total = data.reduce((a, b) => a + b.value, 0);
                const percent = Math.round(item.value / total * 100);
                return (
                    <div key={item.name} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                        <span className="text-ptcm-ink dark:text-gray-300 truncate">{item.name}</span>
                        </div>
                        <div className="font-medium text-ptcm-ink dark:text-gray-100">
                        {percent}%
                        </div>
                    </div>
                );
            })}
        </div>
      </div>
    );
}

export default function StatsPage() {
  const { navigateEmbedded } = useEmbeddedNavigation();
  const { token, user } = useAuth();
  const { family, members } = useFamily();
  const [kpis, setKpis] = useState(null);
  const [error, setError] = useState('');
  const [activeInfoKey, setActiveInfoKey] = useState(null);
  // selectedUserUuid is now derived from viewMode
  const [viewMode, setViewMode] = useState('me'); // 'me' | 'partner' | 'compare'
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const [isHeaderCondensed, setIsHeaderCondensed] = useState(false);

  const myMember = useMemo(
    () => (members || []).find((m) => m.uuid === user?.uuid) || null,
    [members, user?.uuid]
  );

  useAnalytics({
    currentMember: myMember,
    currentFamily: family
  });

  const {
    error: progressError,
    hasCompletedCategory,
    isLoading: isProgressLoading,
    progress
  } = useOnboardingStatus({
    token,
    family,
    member: myMember
  });

  const partnerMember = useMemo(
    () => (members || []).find((m) => m.uuid !== user?.uuid) || null,
    [members, user?.uuid]
  );

  const selectedUserUuid = useMemo(() => {
    if (viewMode === 'me') return user?.uuid;
    if (viewMode === 'partner') return partnerMember?.uuid;
    return null; // For 'compare'
  }, [viewMode, user?.uuid, partnerMember]);

  useEffect(() => {
    let cancelled = false;

    if (!token) {
      setKpis(null);
      return undefined;
    }

    setKpis(null);
    apiFetch('/api/kpis', { token })
      .then((payload) => {
        if (!cancelled) {
          setKpis(payload);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.message || 'Impossible de charger les statistiques.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  // Set default view mode to 'me' if not set
  useEffect(() => {
      if (!viewMode) setViewMode('me');
  }, [viewMode]);

  useEffect(() => {
    const scrollContainer = getAppScrollContainer();
    const onScroll = () => {
      const scrollTop = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
      setIsHeaderCondensed(scrollTop > 120);
    };

    onScroll();
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', onScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', onScroll);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const profileOk = isProfileComplete(myMember);

  // Memoized data based on viewMode
  const selectedData = useMemo(() => {
    // If not loaded yet, return defaults
    if (!kpis) {
        return { kpis: null, byCategory: [], response: null, time: null };
    }

    if (viewMode === 'compare') {
        const meStats = (kpis.members || []).find(x => x.member_uuid === user?.uuid);
        // Find the first other member as partner if partnerMember isn't set, or use partnerMember
        const partnerStats = (kpis.members || []).find(x => x.member_uuid !== user?.uuid);

        return {
            comparison: {
                me: meStats?.time,
                partner: partnerStats?.time
            }
        };
    }

    // Default to user uuid if selectedUserUuid is null (should be handled by derived state but let's be safe)
    const targetUuid = selectedUserUuid || user?.uuid;
    const m = (kpis.members || []).find((x) => x.member_uuid === targetUuid) || null;

    return {
      kpis: m?.kpis ?? null,
      byCategory: m?.by_category ?? [],
      response: m?.response ?? null,
      time: m?.time ?? null
    };
  }, [kpis, viewMode, selectedUserUuid, user?.uuid]);

  const isComparison = viewMode === 'compare';
  const comparisonData = selectedData.comparison;

  // Destructure for Single View (only valid if !isComparison)
  const selectedKpis = selectedData.kpis;
  const selectedByCategory = selectedData.byCategory;
  const selectedResponse = selectedData.response;
  const selectedTime = selectedData.time;

  const byTaskFiltered = useMemo(() => {
    const byTask = Array.isArray(selectedTime?.by_task) ? selectedTime.by_task : [];
    return selectedCategoryId === 'all'
      ? byTask
      : byTask.filter((t) => t.category_id === selectedCategoryId);
  }, [selectedTime, selectedCategoryId]);

  const timeCategories = useMemo(() => {
    const list = Array.isArray(selectedTime?.by_category) ? selectedTime.by_category : [];
    return list
      .filter((c) => (Number(c.minutes_per_week) || 0) > 0.001)
      .sort((a, b) => String(a.category_label).localeCompare(String(b.category_label)));
  }, [selectedTime]);

  const memberProfile = useMemo(
    () => (members || []).find((m) => m.uuid === selectedUserUuid),
    [members, selectedUserUuid]
  );

  if (!profileOk) {
    return (
      <div className="max-w-5xl mx-auto mt-6 px-3 sm:px-4">
        <Card>
          <CardHeader>
            <CardTitle>Statistiques</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-[24px] border border-red-200/70 bg-red-50/90 p-4 text-red-700 dark:bg-red-900 dark:text-red-200">
              Veuillez compléter votre profil de foyer sur l’écran Accueil avant d’accéder aux statistiques.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || progressError) {
    return (
      <div className="max-w-5xl mx-auto mt-6 px-3 sm:px-4">
        <div className="rounded-[24px] border border-red-200/70 bg-red-50/90 p-4 text-red-700 dark:bg-red-900 dark:text-red-200">
          {error || progressError}
        </div>
      </div>
    );
  }

  const partnerDisabled = !partnerMember;

  if (!progress || isProgressLoading) {
    return (
      <StatsLoadingState
        partnerDisabled={partnerDisabled}
        partnerName={displayName(partnerMember)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
    );
  }

  if (!hasCompletedCategory) {
    return (
      <div className="max-w-5xl mx-auto mt-6 px-3 sm:px-4 flex flex-col items-center justify-center min-h-[60vh] text-center space-y-6">
        <div className="mb-2 flex h-24 w-24 items-center justify-center rounded-full bg-ptcm-primary-soft dark:bg-blue-900/20">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-12 h-12 text-ptcm-primary dark:text-blue-400"
          >
            <path d="M3 3v18h18" />
            <path d="M18 17V9" />
            <path d="M13 17V5" />
            <path d="M8 17v-3" />
          </svg>
        </div>

        <div className="space-y-2 max-w-md">
          <h2 className="text-2xl font-bold text-ptcm-ink dark:text-gray-100">
            Vos statistiques sont en attente
          </h2>
          <p className="text-ptcm-muted dark:text-gray-300">
            Pour découvrir la répartition des tâches et de la charge mentale dans votre foyer, vous devez compléter au moins une catégorie du questionnaire.
          </p>
        </div>

        <Button size="lg" onClick={() => navigateEmbedded('/questionnaire')}>
          Commencer le questionnaire
        </Button>
      </div>
    );
  }

  // Determine loading state
  // We are loading if:
  // 1. KPIs are not yet fetched (null)
  // 2. We are in single user view ('me' or 'partner') BUT we haven't identified the target user UUID yet
  //    (This happens if user.uuid is not ready, or partnerMember is not found)
  // Fix: Check for viewMode === 'compare' explicitly to avoid waiting for selectedUserUuid
  const isLoading = !kpis || (viewMode !== 'compare' && !selectedUserUuid);

  if (isLoading) {
    return (
      <StatsLoadingState
        partnerDisabled={partnerDisabled}
        partnerName={displayName(partnerMember)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
    );
  }
  const investmentPercent = selectedKpis?.investment?.percent ?? 0;
  const mentalPercent = selectedKpis?.mental_load?.percent ?? 0;
  const burdenScore = selectedKpis?.burden_score ?? 0;
  const responsePercent = selectedResponse?.percent ?? 0;
  const timeTotals = selectedTime?.totals || {};
  const euros = selectedTime?.euros || {};

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-4 pb-20 space-y-6">
      <div
        className={`sticky z-20 -mx-3 sm:-mx-4 px-3 sm:px-4 border-b border-ptcm-border/70 dark:border-gray-800 bg-ptcm-bg/80 dark:bg-gray-950/80 backdrop-blur-xl transition-all ${isHeaderCondensed ? 'py-1.5' : 'py-2'}`}
        style={{ top: 'var(--ptcm-sticky-top-offset)' }}
      >
        <div className="max-w-5xl mx-auto">
          <ViewSelector
            view={viewMode}
            onChange={setViewMode}
            partnerDisabled={partnerDisabled}
            partnerName={displayName(partnerMember)}
            condensed={isHeaderCondensed}
          />
        </div>
      </div>

      {isComparison ? (
        <ComparisonBlock
            meData={comparisonData?.me}
            partnerData={comparisonData?.partner}
            onOpenInfo={setActiveInfoKey}
        />
      ) : (
        <>
            <div className="space-y-2">
                <div className="flex items-center justify-between px-1">
                <h3 className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">Vue d'ensemble</h3>
                <div className="text-xs text-ptcm-muted">
                    {responsePercent}% complété
                </div>
                </div>

                {/* Horizontal Scroll Container */}
                <div className="flex gap-3 overflow-x-auto pb-4 -mx-3 px-3 sm:mx-0 sm:px-0 snap-x hide-scrollbar">
                <KpiCard title="Exécution" value={`${investmentPercent}%`} color="bg-ptcm-primary-soft/55 dark:bg-blue-900/10 border-ptcm-border dark:border-blue-900/30" infoKey="investment_percent" onOpenInfo={setActiveInfoKey} />
                <KpiCard title="Charge mentale" value={`${mentalPercent}%`} color="bg-ptcm-blush/50 dark:bg-violet-900/10 border-ptcm-border dark:border-violet-900/30" infoKey="mental_load_percent" onOpenInfo={setActiveInfoKey} />
                <KpiCard title="Pénibilité" value={String(burdenScore)} infoKey="burden_score" onOpenInfo={setActiveInfoKey} />
                <div className="w-px bg-ptcm-border dark:bg-gray-800 shrink-0 my-2" />
                <KpiCard title="Temps" value={formatHours(timeTotals.hours_per_week)} subtitle="/ semaine" infoKey="time_week" onOpenInfo={setActiveInfoKey} />
                <KpiCard title="Valo." value={formatEuro(euros?.smic?.per_month)} subtitle="/ mois" infoKey="valuation_smic" onOpenInfo={setActiveInfoKey} />
                <KpiCard title="Valo." value={formatEuro(euros?.smic?.per_year)} subtitle="/ an" infoKey="valuation_smic" onOpenInfo={setActiveInfoKey} />
                </div>
            </div>

            <Card>
                <CardHeader>
                <SectionTitleWithInfo infoKey="by_category_overview" onOpenInfo={setActiveInfoKey}>
                  Aperçu par catégorie
                </SectionTitleWithInfo>
                </CardHeader>
                <CardContent className="space-y-6" style={{ padding: '7px' }}>
                <DualBarsBlock items={selectedByCategory} />
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                <SectionTitleWithInfo infoKey="time_distribution" onOpenInfo={setActiveInfoKey}>
                  Répartition de mon temps (semaine)
                </SectionTitleWithInfo>
                </CardHeader>
                <CardContent>
                <TimeDistributionBlock
                    profile={memberProfile}
                    householdTasksHours={timeTotals.hours_per_week}
                />
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="h-full">
                    <CardHeader>
                        <SectionTitleWithInfo infoKey="time_by_category" onOpenInfo={setActiveInfoKey}>
                          Temps par catégorie
                        </SectionTitleWithInfo>
                    </CardHeader>
                    <CardContent>
                        <CategoryDistributionBlock categories={timeCategories} />
                    </CardContent>
                </Card>
                <Card className="h-full">
                    <CardHeader>
                        <SectionTitleWithInfo infoKey="burden_distribution" onOpenInfo={setActiveInfoKey}>
                          Répartition de la pénibilité
                        </SectionTitleWithInfo>
                    </CardHeader>
                    <CardContent>
                        <BurdenDistributionBlock kpiCategories={selectedByCategory} />
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                <SectionTitleWithInfo infoKey="task_details" onOpenInfo={setActiveInfoKey}>
                  Détail par tâche
                </SectionTitleWithInfo>
                </CardHeader>
                <CardContent className="space-y-4">
                <div className="space-y-2">
                    <div className="text-sm font-medium text-ptcm-ink dark:text-gray-100">Filtrer par catégorie</div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => setSelectedCategoryId('all')}
                            className={`
                                inline-flex items-center justify-center px-4 py-2 rounded-full text-sm font-medium transition-colors border
                                ${selectedCategoryId === 'all'
                                    ? 'bg-ptcm-ink text-white border-ptcm-ink dark:bg-gray-100 dark:text-gray-900 dark:border-gray-100'
                                    : 'bg-white text-ptcm-ink border-ptcm-border hover:bg-ptcm-panel dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700'
                                }
                            `}
                        >
                            Toutes
                        </button>
                        {timeCategories.map((c) => {
                            const Icon = getIconForCategory(c.category_label);
                            return (
                                <button
                                    key={c.category_id}
                                    type="button"
                                    onClick={() => setSelectedCategoryId(c.category_id)}
                                    title={c.category_label}
                                    className={`
                                        inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors border
                                        ${selectedCategoryId === c.category_id
                                            ? 'bg-ptcm-primary text-white border-ptcm-primary'
                                            : 'bg-white text-ptcm-muted border-ptcm-border hover:bg-ptcm-panel dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 dark:hover:bg-gray-700'
                                        }
                                    `}
                                >
                                    <Icon className="w-5 h-5" />
                                </button>
                            );
                        })}
                    </div>
                </div>

                <TaskDetailBlock
                    title="Temps estimé par semaine (par tâche)"
                    items={byTaskFiltered}
                    barClassName="bg-ptcm-primary"
                />
                </CardContent>
            </Card>
        </>
      )}
      <StatsInfoModal infoKey={activeInfoKey} onClose={() => setActiveInfoKey(null)} />
    </div>
  );
}

